/**
 * Le vibe legal, cœur marketing du Tribunal : deux portes (justiciables qui
 * préparent leur dossier avant l'avocat ; avocats qui branchent leur IA), et
 * la démonstration « récit brut → dossier prêt pour l'avocat ».
 *
 * Toujours présenté comme une préparation de dossier et de l'information
 * juridique générale, jamais comme une consultation (loi du 31 décembre 1971).
 */
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ROLE_AVATARS } from './Courtroom';

const FELL = "font-['IM_Fell_English',Georgia,serif]";
const KICK = 'text-[11px] font-bold uppercase tracking-[.16em]';

interface Step { t: string; d: string }
interface Piece { n: string; t: string; ok: boolean }

function useList<T>(key: string): T[] {
  const { t } = useTranslation();
  const v = t(key, { returnObjects: true });
  return Array.isArray(v) ? (v as T[]) : [];
}

/** Rubrique du dossier : apparaît à son tour quand la section entre à l'écran. */
function Section({ i, num, title, children }: { i: number; num: string; title: string; children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 + i * 0.3, duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
      className="min-w-0 pt-3 border-t border-[#d9ccb2]"
    >
      <div className="flex items-baseline gap-2">
        <span className={`${FELL} text-[15px] text-[#a8742a]`}>{num}</span>
        <span className={`${KICK} text-[10.5px] text-[#2a1d12]`}>{title}</span>
      </div>
      <div className="mt-1.5 text-[13px] leading-relaxed text-[#4a3a28]">{children}</div>
    </motion.div>
  );
}
const Bullets = ({ items }: { items: string[] }) => (
  <ul className="m-0 pl-4 list-disc space-y-0.5">{items.map(x => <li key={x}>{x}</li>)}</ul>
);

function Door({ accent, k, extra, cta }: { accent: string; k: 'public' | 'pro'; extra?: ReactNode; cta: ReactNode }) {
  const { t } = useTranslation();
  const steps = useList<Step>(`tribunal.vibe.${k}.steps`);
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col gap-4 p-5 sm:p-8 rounded-2xl bg-[#fffdf8] border border-[#d9ccb2] border-t-[6px] hover:shadow-[0_18px_40px_rgba(42,29,18,.14)] transition-shadow"
      style={{ borderTopColor: accent }}
    >
      <div className={`${KICK}`} style={{ color: accent }}>{t(`tribunal.vibe.${k}.kicker`)}</div>
      <h3 className={`-mt-1 ${FELL} font-normal text-[28px] sm:text-[34px] leading-[1.08] text-[#2a1d12] text-balance`}>
        {t(`tribunal.vibe.${k}.title_1`)}<br />{t(`tribunal.vibe.${k}.title_2`)}
      </h3>
      <p className="text-[15px] leading-relaxed text-[#4a3a28]">{t(`tribunal.vibe.${k}.lede`)}</p>
      <ol className="flex flex-col gap-3.5">
        {steps.map((s, i) => (
          <li key={s.t} className="grid grid-cols-[30px_minmax(0,1fr)] gap-2.5">
            <span className={`${FELL} text-lg`} style={{ color: accent }}>{['I', 'II', 'III'][i]}</span>
            <div>
              <div className="font-bold text-[#2a1d12]">{s.t}</div>
              <div className="text-sm leading-relaxed text-[#4a3a28]">{s.d}</div>
            </div>
          </li>
        ))}
      </ol>
      {extra}
      <div className="mt-auto pt-2 flex flex-wrap items-center gap-4">{cta}</div>
      <p className="text-xs leading-relaxed italic text-[#6e5f4b]">{t(`tribunal.vibe.${k}.foot`)}</p>
    </motion.article>
  );
}

export function VibeLegal() {
  const { t } = useTranslation();
  const files = useList<string>('tribunal.vibe.files');
  const facts = useList<string>('tribunal.vibe.dossier.facts_list');
  const chrono = useList<{ d: string; t: string }>('tribunal.vibe.dossier.chrono_list');
  const pieces = useList<Piece>('tribunal.vibe.dossier.pieces_list');
  const texts = useList<string>('tribunal.vibe.dossier.texts_list');
  const questions = useList<string>('tribunal.vibe.dossier.questions_list');
  const soon = <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#2a1d12]/10">{t('tribunal.vibe.soon')}</span>;

  return (
    <section id="vibe-legal" className="mt-16 lg:mt-20 bg-[#efe3c4] border-y-[3px] border-double border-[#2a1d12] text-[#2a1d12]">
      <div className="max-w-6xl mx-auto px-4 py-10 lg:py-16">
        {/* Titre + comparaison */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_480px] gap-6 lg:gap-14 items-end">
          <div className="min-w-0">
            <div className={`${KICK} text-[#a8742a]`}>{t('tribunal.vibe.kicker')}</div>
            <h2 className={`mt-2 ${FELL} font-normal text-[36px] sm:text-5xl lg:text-[56px] leading-[1.02] text-balance`}>
              {t('tribunal.vibe.title_1')}<br className="hidden sm:block" /> {t('tribunal.vibe.title_2')}
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed text-[#4a3a28]">{t('tribunal.vibe.lede')}</p>
          </div>
          <div className="rounded-xl border border-[#d9ccb2] bg-[#fffdf8] divide-y divide-[#d9ccb2] overflow-hidden">
            {[['compare_coding', 'vibe coding', '#6e5f4b'], ['compare_legal', 'vibe legal', '#a8742a']].map(([k, label, col]) => (
              <div key={k} className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 items-center px-5 py-4">
                <span className="text-sm sm:text-[15px] leading-snug text-[#4a3a28]">{t(`tribunal.vibe.${k}`)}</span>
                <span className={`${FELL} text-lg sm:text-xl text-right`} style={{ color: col }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Démonstration : le récit → le greffier → le dossier */}
        <div id="vibe-demo" className="mt-10 grid grid-cols-1 lg:grid-cols-[380px_110px_minmax(0,1fr)] gap-4 lg:gap-6 items-start scroll-mt-24">
          <div className="min-w-0 flex flex-col gap-2.5">
            <div className={`${KICK} text-[#6e5f4b]`}>{t('tribunal.vibe.story_label')}</div>
            <div className="px-5 py-4 rounded-[16px_16px_16px_4px] bg-[#fffdf8] border border-[#d9ccb2] text-[15px] leading-relaxed">
              {t('tribunal.vibe.story')}
              <motion.span aria-hidden className="inline-block w-0.5 h-4 ml-0.5 align-[-2px] bg-[#2a1d12]" animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {files.map(f => (
                <span key={f} className="inline-flex items-center h-7 px-2.5 rounded-lg bg-[#f6efe0] border border-[#d9ccb2] text-xs text-[#4a3a28]">{f}</span>
              ))}
            </div>
            <div className="text-xs italic text-[#6e5f4b]">{t('tribunal.vibe.story_hint')}</div>
          </div>

          <div className="flex lg:flex-col items-center justify-center gap-3 lg:pt-10">
            <img src={ROLE_AVATARS.clerk} alt="" className="w-11 h-11 lg:w-[72px] lg:h-[72px] rounded-full bg-[#efe3c4] ring-2 ring-[#a8742a]" />
            <div className={`${FELL} italic text-sm text-[#4a3a28] lg:text-center leading-tight`}>{t('tribunal.vibe.clerk')}</div>
            <motion.div
              aria-hidden
              className="flex-1 lg:flex-none lg:w-full h-0.5 bg-[repeating-linear-gradient(90deg,#a8742a_0_10px,transparent_10px_14px)] bg-[length:28px_2px]"
              animate={{ backgroundPositionX: ['0px', '28px'] }}
              transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
            />
            <span className={`${FELL} text-[#a8742a] text-xl`} aria-hidden><span className="lg:hidden">↓</span><span className="hidden lg:inline">→</span></span>
          </div>

          <div className="min-w-0 flex flex-col gap-2.5">
            <div className={`${KICK} text-[#6e5f4b]`}>{t('tribunal.vibe.sent_label')}</div>
            <div className="relative px-4 sm:px-7 pt-5 sm:pt-6 pb-5 bg-[#fffdf8] border border-[#d9ccb2] rounded shadow-[0_1px_0_#d9ccb2,0_12px_0_-6px_#f1e7d2,0_12px_0_-5px_#d9ccb2,0_24px_50px_rgba(42,29,18,.12)]">
              <div className="flex flex-wrap items-end justify-between gap-2 pb-3 border-b-[3px] border-double border-[#2a1d12]">
                <div className="min-w-0">
                  <div className={`${KICK} text-[10.5px] text-[#6e5f4b]`}>{t('tribunal.vibe.dossier.kicker')}</div>
                  <div className={`${FELL} text-[22px] sm:text-[26px] leading-tight mt-1`}>{t('tribunal.vibe.dossier.title')}</div>
                </div>
                <div className="text-xs text-[#6e5f4b] sm:text-right">{t('tribunal.vibe.dossier.kind')}<br />{t('tribunal.vibe.dossier.stake')}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-1 mt-1">
                <Section i={0} num="I" title={t('tribunal.vibe.dossier.facts')}><Bullets items={facts} /></Section>
                <Section i={1} num="II" title={t('tribunal.vibe.dossier.chrono')}>
                  <div className="flex flex-col gap-0.5">
                    {chrono.map(c => (
                      <div key={c.d} className="grid grid-cols-[96px_minmax(0,1fr)] gap-2"><span className="tabular-nums text-[#6e5f4b]">{c.d}</span><span>{c.t}</span></div>
                    ))}
                  </div>
                </Section>
                <Section i={2} num="III" title={t('tribunal.vibe.dossier.pieces')}>
                  <div className="flex flex-col gap-0.5">
                    {pieces.map(p => (
                      <div key={p.n} className="flex items-center gap-2">
                        <span className={`${FELL} w-6 text-[#6e5f4b]`}>{p.n}</span>
                        <span className="flex-1 min-w-0">{p.t}</span>
                        <span className={`text-[11px] font-bold ${p.ok ? 'text-[#4f7a6a]' : 'text-[#9b3a2e]'}`}>
                          {p.ok ? `✓ ${t('tribunal.vibe.dossier.attached')}` : t('tribunal.vibe.dossier.missing_piece')}
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>
                <Section i={3} num="IV" title={t('tribunal.vibe.dossier.texts')}><Bullets items={texts} /></Section>
              </div>
              <div className="sm:pr-48">
                <Section i={4} num="V" title={t('tribunal.vibe.dossier.questions')}><Bullets items={questions} /></Section>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 1.9 }}
                className="mt-4 px-3.5 py-2.5 rounded-lg bg-[#f6e9e3] text-[13px]"
              >
                <b className="text-[#9b3a2e]">{t('tribunal.vibe.dossier.missing_title')}</b> {t('tribunal.vibe.dossier.missing_text')}
              </motion.div>
              <motion.div
                aria-hidden
                initial={{ opacity: 0, scale: 1.8, rotate: -8 }}
                whileInView={{ opacity: 1, scale: 1, rotate: -8 }}
                viewport={{ once: true }}
                transition={{ delay: 2.3, type: 'spring', stiffness: 260, damping: 14 }}
                className={`mt-4 inline-block sm:mt-0 sm:absolute sm:right-8 sm:bottom-24 px-3 py-1.5 border-[3px] border-double border-[#a8742a] text-[#a8742a] bg-[#fffdf8]/85 ${FELL} text-[15px] sm:text-[17px] tracking-wide uppercase`}
              >
                {t('tribunal.vibe.dossier.stamp')}
              </motion.div>
            </div>
          </div>
        </div>

        {/* Les deux portes */}
        <div className="mt-12 lg:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <Door
            accent="#a8742a"
            k="public"
            cta={<>
              <span className="h-12 px-5 inline-flex items-center rounded-xl bg-[#d9a441] text-[#2a1d12] font-bold cursor-default" aria-disabled="true">
                {t('tribunal.vibe.public.cta')}{soon}
              </span>
              <a href="#vibe-demo" className="font-bold text-sm text-[#2a1d12] hover:underline">{t('tribunal.vibe.public.cta2')} →</a>
            </>}
          />
          <Door
            accent="#3d5a73"
            k="pro"
            extra={
              <div className="flex flex-wrap gap-1.5">
                {['Claude', 'ChatGPT', 'Mistral', t('tribunal.vibe.pro.any_client')].map(m => (
                  <span key={m} className="h-[30px] px-3 inline-flex items-center rounded-full border border-[#d9ccb2] bg-[#f6efe0] text-[13px] font-semibold">{m}</span>
                ))}
              </div>
            }
            cta={<>
              <a href="/#mcp" className="h-12 px-5 inline-flex items-center rounded-xl bg-[#2a1d12] hover:bg-[#4a3a28] text-[#f6efe0] font-bold">{t('tribunal.vibe.pro.cta')}</a>
              <span className="font-bold text-sm text-[#2a1d12]/70">{t('tribunal.vibe.pro.cta2')}{soon}</span>
            </>}
          />
        </div>

        {/* Le pont vers les avocats du Tribunal */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 px-5 sm:px-7 py-4 sm:py-5 rounded-2xl bg-[#2a1d12] text-[#f6efe0]">
          <div className="flex items-center gap-3 min-w-0 sm:flex-1">
            <div className="flex shrink-0">
              <img src={ROLE_AVATARS.prosecution} alt="" className="w-10 h-10 sm:w-[52px] sm:h-[52px] rounded-full bg-[#efe3c4] ring-2 ring-[#9b3a2e]" />
              <img src={ROLE_AVATARS.defense} alt="" className="-ml-2.5 w-10 h-10 sm:w-[52px] sm:h-[52px] rounded-full bg-[#efe3c4] ring-2 ring-[#3d5a73]" />
            </div>
            <div className="min-w-0">
              <div className={`${FELL} text-lg sm:text-[22px] leading-tight`}>{t('tribunal.vibe.bridge.title')}</div>
              <div className="text-[13px] sm:text-sm text-[#d6c8ad] mt-0.5">{t('tribunal.vibe.bridge.text')}</div>
            </div>
          </div>
          <a href="#affaires" className="shrink-0 font-bold text-sm text-[#d9a441] hover:underline">{t('tribunal.vibe.bridge.cta')} →</a>
        </div>

        <p className="mt-5 text-[11px] sm:text-xs leading-relaxed text-[#6e5f4b] text-center max-w-4xl mx-auto">{t('tribunal.vibe.legal')}</p>
      </div>
    </section>
  );
}
